/**
 * cache/types.ts — the CacheLayoutV1 contract.
 *
 * A **cache** is a short instanced dungeon. Its layout is generated in three layers
 * (see docs/design/caches-dungeon-research.md):
 *
 *   MISSION   cyclic graph grammar → a task graph, solvable BY CONSTRUCTION
 *   SPACE     graph → integer cell grid → this file's CacheLayoutV1
 *   DRESSING  layout → geometry, per theme (client only)
 *
 * This module is the seam between layers 2 and 3, and between client and server:
 * both regenerate the SAME layout from (site, epoch, theme, tier), so nothing about
 * cache geometry is ever transmitted or persisted. Keep it small and JSON-clean.
 *
 * COORDINATES. Rooms carry both their working-grid cell (`cu`,`cv` — integers, the
 * authority for adjacency and flood-fill) and a pad-local centre in metres (`x`,`z`
 * — derived, the authority for geometry). Pad-local means: origin at the pad world's
 * +Y pole, +x east, +z south, before the sphere curvature the client applies.
 *
 * SHARED-CODE DISCIPLINE: the generator is integer grid math and uses NO trig. The
 * pmath-ban gate covers this directory.
 */

export const CACHE_LAYOUT_VERSION = 1 as const;

/** Aesthetic family. Chosen EARLY (it constrains every later pool), applied LATE. */
export const CACHE_THEMES = ["ruin", "vault", "hollow"] as const;
export type CacheTheme = (typeof CACHE_THEMES)[number];

/**
 * What a room is FOR. The mission layer assigns these; the dressing layer maps them
 * to geometry per theme. Deliberately gameplay-flavoured, not shape-flavoured — a
 * "gate" is a room you must open, whatever it looks like.
 */
export const CACHE_ROLES = [
  "entrance",  // where you come in; always exactly one
  "transit",   // connective tissue — the default non-terminal resolution
  "arena",     // an open room sized for a fight
  "gate",      // holds the barrier an edge is locked by
  "key",       // holds the thing that opens a gate
  "treasure",  // a reward cache
  "boss",      // the miniboss arena; always exactly one
  "shortcut",  // a one-way link back toward the entrance
  "vista",     // a sightline room: shows you somewhere you cannot yet reach
] as const;
export type CacheRole = (typeof CACHE_ROLES)[number];

/** How two rooms connect. `drop` is ONE-WAY (a→b only); the rest are bidirectional. */
export const CACHE_EDGE_KINDS = ["door", "tunnel", "gate", "drop", "bridge"] as const;
export type CacheEdgeKind = (typeof CACHE_EDGE_KINDS)[number];

/** Abstract prop slot. The theme picks the actual mesh. */
export const CACHE_PROP_KINDS = ["cover", "light", "loot", "hazard", "marker"] as const;
export type CachePropKind = (typeof CACHE_PROP_KINDS)[number];

export interface CacheRoom {
  /** Index into {@link CacheLayoutV1.rooms} — equal to the array position. */
  i: number;
  role: CacheRole;
  /** Dressing tag within the role (theme pools key off this). */
  archetype: string;
  /** Working-grid cell — the authority for adjacency and reachability. */
  cu: number;
  cv: number;
  /** Pad-local centre, metres (derived from the cell). */
  x: number;
  z: number;
  /** Footprint, metres. */
  w: number;
  d: number;
  /**
   * Floor height above the pad datum, metres — the BFS-depth terrace ladder.
   *
   * SHARED because it is not a look: the dressing stands its geometry on it, and the
   * server has to seed a garrison on the SAME surface the player walks. It lived in
   * `client/src/cache/theme/common.ts` through P7, which is exactly why P6b had to ship
   * live with flat floors (the drones would have stood on the datum under a terraced
   * player). Minted by {@link import("./layout.js").generateCache}; see
   * `roomFloorHeights` there for the ladder and its per-theme constants.
   *
   * Always ≥ 0, and the entrance is always the minimum (normalised to 0 unless a `drop`
   * edge pushed something lower first).
   */
  floorH: number;
}

export interface CacheEdge {
  a: number;
  b: number;
  kind: CacheEdgeKind;
  /**
   * Room index of the KEY that opens this edge, or -1 when it is never locked.
   * This is the Unexplored lock/key reference: the pair travels together through
   * every graph rewrite, which is what makes solvability structural rather than
   * something we generate-and-check.
   */
  lock: number;
  /** Working-grid cells the connection occupies between the two rooms. */
  cells: readonly (readonly [number, number])[];
}

/** An encounter slot. The CONCRETE enemy kinds are Phase 5's job (encounters.ts). */
export interface CacheSpawn {
  room: number;
  /** Abstract encounter budget for this room — scales with tier and role. */
  budget: number;
}

export interface CacheProp {
  room: number;
  kind: CachePropKind;
  /** Offset from the room centre, metres. */
  u: number;
  v: number;
  /** Facing, radians. Data only — no trig is taken in shared. */
  yaw: number;
}

export interface CacheLayoutV1 {
  v: typeof CACHE_LAYOUT_VERSION;
  /** Deterministic site id (planet + site index). */
  site: string;
  /** Reroll counter — a fresh epoch is a fresh dungeon at the same site. */
  epoch: number;
  theme: CacheTheme;
  /** 0 = short run, 1+ = long run. */
  tier: number;
  cycle: string;
  /** Working-grid size (cells per axis). */
  grid: number;
  /** Metres per working-grid cell. */
  cell: number;
  rooms: CacheRoom[];
  edges: CacheEdge[];
  spawns: CacheSpawn[];
  props: CacheProp[];
  /** Room index of the entrance. */
  entrance: number;
  /** Room index of the boss arena. */
  boss: number;
  /** Pad-local half-extent, metres — sizes the pad world and its curvature budget. */
  extent: number;
}
